import type { SwingMetrics } from '../types.js';
import type { PersonalBaseline } from './baseline.js';
import type { RootCauseHit } from './causalGraph.js';
import { consolidateHits, inferRootCauses } from './causalGraph.js';
import type { Narrative } from './narrative.js';
import { synthesize } from './narrative.js';
import { rules as allRules } from './rules.js';
import type { SessionStats } from './sessionAggregates.js';
import { computeSessionStats } from './sessionAggregates.js';
import type { Aggregates, Rule, RuleHit } from './types.js';
import { SEVERITY_RANK, hasRequiredData } from './types.js';

/**
 * Evaluate every coaching rule against a single swing + its session aggregates.
 *
 * Rules are skipped (not failed) when:
 *  - the rule is shot-type specific and the swing's shot type differs
 *  - the rule requires aggregate fields not present on the passed aggregates
 *  - the rule's condition throws (defensive — surfaced via console.warn)
 *
 * Output is sorted `warning` > `tip` > `info`, then by rule id for stability.
 *
 * NOTE: per-swing consolidation by root causes happens in
 * {@link evaluateSession}. Calling `evaluateSwing` on a single swing returns
 * raw hits only (causes are most informative over N swings).
 */
export function evaluateSwing(swing: SwingMetrics, aggregates: Aggregates): RuleHit[] {
  const hits: RuleHit[] = [];
  for (const rule of allRules) {
    if (rule.shotType !== undefined && swing.shotType !== undefined) {
      if (rule.shotType !== swing.shotType) continue;
    } else if (rule.shotType !== undefined && swing.shotType === undefined) {
      continue;
    }
    if (!hasRequiredData(aggregates, rule.requiredData)) continue;
    let fired = false;
    try {
      fired = rule.condition(swing, aggregates);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn(`[coaching] rule ${rule.id} threw`, err);
      continue;
    }
    if (fired) hits.push({ rule, fired: true });
  }
  hits.sort((a, b) => {
    const bySeverity = SEVERITY_RANK[a.rule.severity] - SEVERITY_RANK[b.rule.severity];
    if (bySeverity !== 0) return bySeverity;
    return a.rule.id.localeCompare(b.rule.id);
  });
  return hits;
}

/**
 * Legacy alias retained for Sprint 2 callers. Prefer {@link evaluateSwing}.
 */
export const evaluate = evaluateSwing;

export interface SessionEvaluation {
  readonly perSwing: readonly RuleHit[][];
  readonly session: readonly RuleHit[];
}

/**
 * Full session coaching output: per-swing hits, inferred root causes,
 * session stats, and a friendly narrative block. This is the primary
 * entry point the UI should consume.
 */
export interface SessionCoachingReport {
  readonly perSwing: readonly RuleHit[][];
  readonly session: readonly RuleHit[];
  readonly rootCauses: readonly RootCauseHit[];
  readonly stats: SessionStats;
  readonly narrative: Narrative;
}

/**
 * Evaluate an entire session: runs every rule against every swing, infers
 * root causes across the combined hit set, computes aggregate stats, and
 * produces a pt-BR narrative.
 *
 * `baseline` is optional — when supplied, the session stats will include
 * `progressionVsBaseline` diffs.
 */
export function evaluateSession(
  swings: readonly SwingMetrics[],
  aggregates: Aggregates,
  baseline?: PersonalBaseline,
): SessionCoachingReport {
  const perSwingRaw: RuleHit[][] = swings.map((s) => evaluateSwing(s, aggregates));

  // Cross-session causal inference runs on the UNION of hits.
  const allHits: RuleHit[] = perSwingRaw.flat();
  const rootCauses = inferRootCauses(allHits);

  // Consolidate at the swing level so each per-swing view hides symptoms
  // a dominant cause already explains.
  const perSwing: RuleHit[][] = perSwingRaw.map((hits) => consolidateHits(hits, rootCauses));

  // Session-only pass (aggregate-driven rules whose id starts with "session.").
  const neutralSwing: SwingMetrics = {
    contactFrame: 0,
    contactTimeMs: 0,
    elbowAngleAtContactDeg: aggregates.medianElbowAtContactDeg,
    peakWristAngularVelocityRadPerSec: aggregates.medianPeakWristAngVel,
    swingDurationMs: 0,
    elbowAngleSeriesDeg: [],
  };
  const sessionHits: RuleHit[] = [];
  for (const rule of allRules) {
    if (!rule.id.startsWith('session.')) continue;
    if (!hasRequiredData(aggregates, rule.requiredData)) continue;
    try {
      if (rule.condition(neutralSwing, aggregates)) {
        sessionHits.push({ rule, fired: true });
      }
    } catch {
      /* swallow — session rules are advisory */
    }
  }
  sessionHits.sort((a, b) => SEVERITY_RANK[a.rule.severity] - SEVERITY_RANK[b.rule.severity]);

  // Per-swing causes (each swing's hits only) — used to compute systemic
  // issues (causes that show up in >= 40% of swings).
  const perSwingCauses = perSwingRaw.map((hits) => inferRootCauses(hits));

  const stats = computeSessionStats(swings, {
    perSwingCauses,
    baseline,
  });

  const narrative = synthesize(allHits, rootCauses, stats);

  return {
    perSwing,
    session: sessionHits,
    rootCauses,
    stats,
    narrative,
  };
}

/** Utility: return the full frozen rule list (re-exported for convenience). */
export function getRules(): readonly Rule[] {
  return allRules;
}
